// Auto-fill proposals. "Auto-fill the rest" no longer writes the plan: the engine's output is
// diffed against the current plan, shown as ghost cards, and only the terms a student accepts
// are merged in. Merging only ever adds items; nothing already in the plan is removed or moved.
// Callers keep the engine output and re-diff it against the live plan, so accepted terms and
// courses the student placed by hand drop out of the proposal on their own.

import type { Semester, SemesterItem } from '@/app/types/interfaces';
import { normalize } from '@/app/services/degreeRequirementService';
import { itemCode } from './plannerModel';

/** A term as the auto-fill engine returns it (names like "Fall 2027"). */
export interface ProposedTermInput {
  name: string;
  courses: SemesterItem[];
}

export interface ProposalTerm {
  /** Display and matching key, e.g. "Fall 2027". */
  name: string;
  /** The existing plan term with the same name, or null when accepting creates the term. */
  semesterNumber: number | null;
  items: SemesterItem[];
  credits: number;
}

const SEASON_ORDER: Record<string, number> = { winter: 0, spring: 1, summer: 2, fall: 3 };

const termKey = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

/** Chronological sort key for "Season YYYY" names; unknown names sort last in input order. */
export function termOrderKey(name: string): number {
  const match = /(winter|spring|summer|fall)\s+(\d{4})/i.exec(name || '');
  if (!match) return Number.POSITIVE_INFINITY;
  return Number(match[2]) * 4 + SEASON_ORDER[match[1].toLowerCase()];
}

const itemCredits = (item: SemesterItem): number => {
  if (item.kind === 'course_list') return (Number(item.credits) || 0) * (item.quantity || 1);
  return Number(item.credits) || 0;
};

/**
 * What the proposal would add to the current plan, grouped by term.
 * - A course is proposed only if it isn't completed, isn't already anywhere in the plan
 *   (directly or as a placeholder's chosen course), and wasn't proposed in an earlier term.
 * - Placeholders are matched by list: if the plan already holds N placeholders for a list,
 *   the first N proposed placeholders for that list are treated as already covered.
 * Terms with nothing left to add are dropped.
 */
export function diffProposal(current: Semester[], proposed: ProposedTermInput[], completed: Set<string>): ProposalTerm[] {
  const taken = new Set<string>(completed);
  const heldLists = new Map<string, number>();
  current.forEach((semester) => (semester.courses || []).forEach((item) => {
    const code = itemCode(item);
    if (code) taken.add(code);
    if (item.kind === 'course_list') heldLists.set(item.mappingName, (heldLists.get(item.mappingName) || 0) + (item.quantity || 1));
  }));
  const byName = new Map(current.map((semester) => [termKey(semester.name), semester.number] as const));

  const out: ProposalTerm[] = [];
  proposed.forEach((term) => {
    const items: SemesterItem[] = [];
    (term.courses || []).forEach((item) => {
      if (item.kind === 'course_list') {
        const held = heldLists.get(item.mappingName) || 0;
        if (held > 0) { heldLists.set(item.mappingName, held - 1); return; }
        const chosen = item.selectedCourseCode ? normalize(item.selectedCourseCode) : null;
        if (chosen && taken.has(chosen)) return;
        if (chosen) taken.add(chosen);
        items.push(item);
        return;
      }
      const code = normalize(item.code);
      if (!code || taken.has(code)) return;
      taken.add(code);
      items.push(item);
    });
    if (!items.length) return;
    const key = termKey(term.name);
    const existing = out.find((t) => termKey(t.name) === key);
    if (existing) {
      existing.items.push(...items);
      existing.credits += items.reduce((sum, item) => sum + itemCredits(item), 0);
      return;
    }
    out.push({ name: term.name.trim(), semesterNumber: byName.get(key) ?? null, items, credits: items.reduce((sum, item) => sum + itemCredits(item), 0) });
  });
  return out.sort((a, b) => termOrderKey(a.name) - termOrderKey(b.name));
}

/**
 * Merge accepted proposal terms into a copy of the plan. `accept` is 'all' or the names of
 * the terms to accept. Missing terms are created with the next free semester number, and
 * the result is ordered chronologically by name. Existing items are never touched.
 */
export function applyProposal(current: Semester[], proposal: ProposalTerm[], accept: 'all' | string[]): Semester[] {
  const next = JSON.parse(JSON.stringify(current)) as Semester[];
  const wanted = accept === 'all' ? null : new Set(accept.map(termKey));
  let nextNumber = next.reduce((max, semester) => Math.max(max, semester.number), 0) + 1;
  proposal.forEach((term) => {
    if (wanted && !wanted.has(termKey(term.name))) return;
    let semester = next.find((s) => termKey(s.name) === termKey(term.name));
    if (!semester) {
      semester = { number: nextNumber++, name: term.name, courses: [], totalCredits: 0 };
      next.push(semester);
    }
    if (!Array.isArray(semester.courses)) semester.courses = [];
    const number = semester.number;
    semester.courses.push(...term.items.map((item) => ({ ...JSON.parse(JSON.stringify(item)), semester: number }) as SemesterItem));
  });
  next.forEach((semester) => {
    semester.totalCredits = (semester.courses || []).reduce((sum, item) => sum + (item.kind === 'course' ? Number(item.credits) || 0 : 0), 0);
  });
  // Stable chronological order; unknown names keep their place after dated terms.
  return next
    .map((semester, i) => ({ semester, i }))
    .sort((a, b) => (termOrderKey(a.semester.name) - termOrderKey(b.semester.name)) || a.i - b.i)
    .map(({ semester }) => semester);
}

export function proposalCounts(proposal: ProposalTerm[]): { items: number; terms: number; credits: number } {
  return {
    items: proposal.reduce((sum, term) => sum + term.items.length, 0),
    terms: proposal.length,
    credits: proposal.reduce((sum, term) => sum + term.credits, 0),
  };
}
